import { useState, useEffect } from 'react'
import {
  X,
  MessageSquare,
  Send,
  CheckCircle,
  Mail,
  Bug,
  Lightbulb,
  User
} from 'lucide-react'
import styles from './ContactFeedbackModal.module.css'

type FeedbackType = 'contact' | 'bug' | 'feature' | 'general'

interface UserContext {
  developerId: string
  email: string
  name?: string
}

interface ContactFeedbackModalProps {
  isOpen: boolean
  onClose: () => void
  context?: string
}

export default function ContactFeedbackModal({
  isOpen,
  onClose,
  context = 'general'
}: ContactFeedbackModalProps) {
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('contact')
  const [message, setMessage] = useState('')
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [responseMessage, setResponseMessage] = useState('')
  const [userContext, setUserContext] = useState<UserContext | null>(null)
  const [loadingUser, setLoadingUser] = useState(true)
  const [wantsResponse, setWantsResponse] = useState(false)


  useEffect(() => {
    if (!isOpen) return
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen])

  useEffect(() => {
    if (isOpen) fetchUserContext()
  }, [isOpen])

  const fetchUserContext = async () => {
    setLoadingUser(true)
    try {
      // Fetch user context from /api/me. The route returns
      // { developer: { id, name, email, ... }, stats: {...} }.
      // Middleware already injects identity headers, so no extra auth here.
      const res = await fetch('/api/me', { cache: 'no-store' })
      if (res.ok) {
        const data = await res.json()
        const d = data.developer
        if (d?.email) {
          setUserContext({
            developerId: d.id,
            email: d.email,
            name: d.name || null,
          })
        } else {
          setUserContext(null)
        }
      } else {
        setUserContext(null)
      }
    } catch {
      setUserContext(null)
    } finally {
      setLoadingUser(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!message.trim()) return

    const submissionEmail = userContext?.email || email.trim() || null
    setSubmitting(true)

    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          feedbackType,
          message: message.trim(),
          email: submissionEmail,
          name: userContext?.name || null,
          developerId: userContext?.developerId || null,
          context,
          userAgent: navigator.userAgent,
          wantsResponse
        })
      })

      if (response.ok) {
        const isContact = feedbackType === 'contact'
        setResponseMessage(
          isContact
            ? "We've received your message and will get back to you shortly."
            : 'Thank you for your feedback! We review every submission.'
        )
        setSubmitted(true)
      } else {
        throw new Error('Failed to submit')
      }
    } catch {
      setResponseMessage("Thanks for reaching out! We'll review your message.")
      setSubmitted(true)
    } finally {
      setSubmitting(false)
    }
  }

  const handleReset = () => {
    setSubmitted(false)
    setMessage('')
    setEmail('')
    setFeedbackType('contact')
    setResponseMessage('')
    setWantsResponse(false)
  }

  const handleClose = () => {
    onClose()
    setTimeout(handleReset, 300)
  }

  const getPlaceholder = () => {
    switch (feedbackType) {
      case 'contact': return 'How can we help you?'
      case 'bug': return "Describe what's not working..."
      case 'feature': return 'What would you like to see?'
      default: return 'Share your thoughts...'
    }
  }

  const getTypeButtonClass = (type: FeedbackType) => {
    if (feedbackType !== type) return styles.typeButton
    switch (type) {
      case 'bug': return `${styles.typeButton} ${styles.activeError}`
      case 'feature': return `${styles.typeButton} ${styles.activeWarning}`
      default: return `${styles.typeButton} ${styles.active}`
    }
  }

  if (!isOpen) return null

  return (
    <>
      <div className={styles.overlay} onClick={handleClose} />

      <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Contact and Feedback">
        {submitted ? (
          <div className={styles.successState}>
            <div className={styles.successIcon}>
              <CheckCircle size={32} />
            </div>
            <h3 className={styles.successTitle}>
              {feedbackType === 'contact' ? 'Message Sent!' : 'Thank you!'}
            </h3>
            <p className={styles.successMessage}>{responseMessage}</p>
            <div className={styles.successActions}>
              <button onClick={handleReset} className={styles.sendMoreButton}>
                Send Another Message
              </button>
              <button onClick={handleClose} className={styles.closeSuccessButton}>
                Close
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.header}>
              <div className={styles.headerLeft}>
                <MessageSquare size={24} className={styles.headerIcon} />
                <div>
                  <h3 className={styles.headerTitle}>Contact & Feedback</h3>
                  <p className={styles.headerSubtitle}>Get in touch or share your thoughts</p>
                </div>
              </div>
              <button onClick={handleClose} className={styles.closeButton} aria-label="Close">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className={styles.body}>
              {!loadingUser && (
                <div className={styles.userContext}>
                  {userContext ? (
                    <div className={styles.userInfo}>
                      <div className={styles.userInfoRow}>
                        <User size={14} className={styles.userInfoIcon} />
                        <span className={styles.userInfoLabel}>Sending as:</span>
                        <span className={styles.userInfoValue}>{userContext.email}</span>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.fieldGroup} style={{ marginBottom: 0 }}>
                      <label htmlFor="feedback-email" className={styles.label}>
                        Your Email
                      </label>
                      <div className={styles.inputWithIcon}>
                        <Mail size={16} className={styles.inputIcon} />
                        <input
                          id="feedback-email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="your@email.com"
                          className={styles.inputWithIconField}
                        />
                      </div>
                      <p className={styles.hint}>Optional — provide if you'd like a response</p>
                    </div>
                  )}
                </div>
              )}

              <div className={styles.typeSection}>
                <label className={styles.label}>What can we help with?</label>
                <div className={styles.typeGrid}>
                  <button type="button" onClick={() => setFeedbackType('contact')} className={getTypeButtonClass('contact')} aria-pressed={feedbackType === 'contact'}>
                    <Mail size={18} className={styles.typeIcon} />
                    <span className={styles.typeLabel}>Contact Us</span>
                  </button>
                  <button type="button" onClick={() => setFeedbackType('bug')} className={getTypeButtonClass('bug')} aria-pressed={feedbackType === 'bug'}>
                    <Bug size={18} className={styles.typeIcon} />
                    <span className={styles.typeLabel}>Bug Report</span>
                  </button>
                  <button type="button" onClick={() => setFeedbackType('feature')} className={getTypeButtonClass('feature')} aria-pressed={feedbackType === 'feature'}>
                    <Lightbulb size={18} className={styles.typeIcon} />
                    <span className={styles.typeLabel}>Feature Request</span>
                  </button>
                  <button type="button" onClick={() => setFeedbackType('general')} className={getTypeButtonClass('general')} aria-pressed={feedbackType === 'general'}>
                    <Send size={18} className={styles.typeIcon} />
                    <span className={styles.typeLabel}>General</span>
                  </button>
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <label htmlFor="feedback-message" className={styles.label}>
                  Your Message <span className={styles.required}>*</span>
                </label>
                <textarea
                  id="feedback-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={getPlaceholder()}
                  className={styles.textarea}
                  rows={5}
                  required
                />
              </div>

              <div className={styles.responseCheckbox}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={wantsResponse}
                    onChange={(e) => setWantsResponse(e.target.checked)}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkboxText}>I'd like a response back</span>
                </label>
              </div>

              <div className={styles.actions}>
                <button type="submit" disabled={!message.trim() || submitting} className={styles.submitButton}>
                  {submitting ? (
                    <>
                      <div className={styles.spinner} />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Send Message
                    </>
                  )}
                </button>
                <button type="button" onClick={handleClose} className={styles.cancelButton}>
                  Cancel
                </button>
              </div>

              <div className={styles.infoBanner}>
                <p className={styles.infoBannerText}>
                  Questions, issues, or ideas — we read every message!
                  {wantsResponse && !userContext && !email.trim() && (
                    <><br /><strong>Please provide your email above to receive a response.</strong></>
                  )}
                  {wantsResponse && (userContext || email.trim()) && (
                    <><br />We'll get back to you within 1-2 business days.</>
                  )}
                </p>
              </div>
            </form>
          </>
        )}
      </div>
    </>
  )
}
